import { StepBase } from './StepBase';
import { PageStepType } from './PageTypes';
import { StringOptions } from '../utils/StringOptions';
import { LogicEngine } from '../LogicEngine/LogicEngine';
import { AuditLog } from '../Participant/AuditLog';
import { QueryLog } from '../Participant/QueryLog';

import { DataServices } from '../utils/DataServices';
import { DataDictionary, DataDictionaryTable, FieldError } from '../utils/DataDictionary';
import { DataRecord } from '../utils/DataRecord';

import { DataFormatter } from '../dataformats/DataFormatter';
import { DataTools } from '../utils/DataTools';
import { TaskManager } from '../Tasks/TaskManager';
import { DataQuery } from '../utils/DataQuery';
import { StepConditional } from './StepConditional';

// Library for Events that are strongly typed
// see https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';
import { form } from 'blessed';
import { mode } from 'mathjs';

export class StepFormField extends StepBase
{
}

export interface StepFromLockStatus
{
	/* Show the PI Lock Form on the bottom of the page */
	show_pi_lock: boolean;

	/* Show the Site Lock form on the bottom of the page */
	show_edit_lock: boolean;

	/* When true, allow the form to be edit as normal by thosing the edit buttons
	When false, show the edit button gray, it can't be clicked with title "Locked"*/
	form_edit_allow: boolean;

	/* Display this message on the top of the form if not undefined or "" */
	form_edit_message: string;

	/* Username that locked the record with Site / PI Permissions */
	pi_lock_by?: string;

	/* Date and Time the record was locked at the site */
	pi_lock_dt?: Date;

	/* Username that locked the record with Site / PI Permissions */
	lock_by?: string;

	/* Date and Time the record was locked at the site */
	lock_dt?: Date;

	/* Is the record fully locked? */
	lock_status?: boolean;
}

export class StepForm extends StepBase
{
	fields: StepForm.FieldInfo[] = [];
	logicRef: LogicEngine | undefined;
	dataDictionary: DataDictionary | undefined;
	dataFormatter: DataFormatter | undefined;

	/** Determine if the form is edit only */
	is_edit_mode: boolean = false;

	/** Determine if the form is a query mode for data monitor */
	is_query_mode: boolean = false;

	/** Lock Status */
	lock_status: StepFromLockStatus | undefined;

	/** Reference to the record that is either new or editing for this form */
	record: DataRecord | undefined;

	/** Reference to the DataDictionary Table for this record */
	tableDef: DataDictionaryTable | undefined;

	/** List of fields that are force disabled */
	fieldsEnabled: { [key: string]: boolean } = {};

	/** Fired at the time of a save event with the results */
	evOnSave: SubEvent<FieldError[]> = new SubEvent();

	/** Additional conditions that must be validated on the form */
	conditions: StepConditional[] = [];

	/** Additional conditions that must be scored */
	scoring: StepConditional[] = [];

	/** List of history for the form based on the edits saved through the audit log */
	formHistory: DataRecord[] = [];

	/** List of query data for the form */
	formQuery: DataRecord[] = [];

	/** List of lock history for the form */
	lockHistory: DataRecord[] = [];

	testField: number = 111;

	auditTimer: any = undefined;


	/** Return the current value of a field as it would be stored in the database.
	 * Lookup items return the code, boolean items return true/false,
	 * Date items return a Date object, etc...
	 */
	getValueDatabase(fieldName: string): any
	{
		if (!this.record) return undefined;
		return this.record.getValueDatabase(fieldName);
	}

	/** Return the text formatted version of the value for a given field.
	 * A boolean value is return as "Yes" or "No", for example.
	 */
	getValueFormatted(fieldName: string): string
	{
		if (!this.record) return '';
		return this.record.getValueFormatted(fieldName);
	}

	/** Returns the units of a field bby name */
	getValueUnits(fieldName: string): string
	{
		if (!this.record) return '';
		return this.record.getValueUnits(fieldName);
	}

	/** Returns the alignment of a field by name */
	getValueAlign(fieldName: string): string
	{
		if (!this.record) return '';
		return this.record.getValueAlign(fieldName);
	}

	getValueOptions(fieldName: string): StringOptions
	{
		if (!this.record) return new StringOptions("");
		return this.record.getValueOptions(fieldName);
	}

	/**
	 * Goes through all fields in this form to handle the scoring system.
	 * The scroring system works but having a list of conditions and each condition applies a
	 * score.  For example, if a condition is {{ equals(answer, 5) }} and the score is 3
	 * then the score adds 3 if the condition is true.   This can be used to calculate totals,
	 * handle scoring for medical evaluation forms, or other types of field logic.
	 *
	 * Some field in the data will hold the resulting score.
	 *
	 * See "Conditional" "Score" command for help.
	 *
	 * @returns void
	 */
	scoreForm(): void
	{
		if (!this.record) return;
		if (!this.logicRef) return;

		/** Reset values to 0 first */
		for (const c of this.scoring)
		{
			this.record.data[c.field] = 0;
		}

		for (const c of this.scoring)
		{
			let conditionRaw = c.conditionalText;
			let field = c.field;
			let scoreRaw = c.option_1;

			console.log('StepForm Scoring 1 Condition=', conditionRaw, ' Field=', field, 'Amount=', scoreRaw,);
			let condition = this.logicRef.processTextReplacement(conditionRaw);
			let score = DataTools.getNumber(this.logicRef.processTextReplacement(scoreRaw));

			//  Convert the result into a boolean
			let df = new DataFormatter();
			const boolValue = df.unformatData('Boolean', condition);

			if (boolValue)
			{
				this.record.data[field] += score;
			}
		}

		for (const c of this.scoring)
		{
			console.log('StepForm Scoring set ', c.field, '=', this.record.data[c.field]);
		}
	}

	/** Loop through all the conditions for custom validation */
	async validateAdditionalConditions(ignore_warnings: boolean): Promise<FieldError[]>
	{
		let results: FieldError[] = [];
		for (const c of this.conditions)
		{

			if (ignore_warnings && c.conditionalType === 'warning') continue;
			//  Field that is affected
			const field: string = c.raw.field_id ? c.raw.field_id : '';

			if (this.fieldsEnabled[field] === false) continue;

			const value = this.logicRef?.processTextReplacement(c.option_1.valueOf());

			//  Convert the result into a boolean
			let df = new DataFormatter();
			const boolValue = df.unformatData('Boolean', value);

			console.log('StepFrom Additional field validation ', field, ' Logic=', c.option_1, ' = ', value);

			const error_msg = this.logicRef ? this.logicRef.processTextReplacement(c.text) : 'Invalid value';

			if (!boolValue)
			{
				results.push({
					field_name: field,
					error_message: error_msg.replace('The value entered', 'The ' + field + ' value'),
					field_status: c.conditionalType,
				});
			}
		}

		return results;
	}

	// Save fields for a partial form and return validation.
	//
	async checkPage(ignore_warnings: boolean): Promise<FieldError[]>
	{
		if (this.logicRef?.fnHasGroup('DataMonitor'))
		{
			console.log('StepForm Unable to save form, data monitor mode found.');
			return [];
		}

		if (this.tableDef && this.logicRef && this.record)
		{
			console.log('StepForm checkPage fixRecord Before=', this.record.data);
			await this.record.fixRecord();
			console.log('StepForm checkPage fixRecord After=', this.record.data);

			let results = await this.tableDef.validateRecord(this.record, this.fieldsEnabled);
			let results2 = await this.validateAdditionalConditions(ignore_warnings);

			let resultsAll = [];
			resultsAll.push(...results);
			resultsAll.push(...results2);

			if (resultsAll.length > 0)
			{
				// Form has validation problems, don't save to server
				this.evOnSave.emit(resultsAll);
				return resultsAll;
			}

			this.scoreForm();
			return resultsAll;
		} else
		{
			let results: FieldError[] = [];
			this.evOnSave.emit(results);
			return results;
		}
	}

	/** Save Record
	 * @returns Returns a list of validation errors
	 */
	async save(ignore_warnings: boolean): Promise<FieldError[]>
	{
		if (this.logicRef?.fnHasGroup('DataMonitor'))
		{
			console.log('StepForm Unable to save form, data monitor mode found.');
			return [];
		}

		if (this.tableDef && this.logicRef && this.record)
		{
			if (LogicEngine.debugOutput)
				console.log('StepForm Save Before=', this.record.data);

			await this.record.fixRecord();

			if (LogicEngine.debugOutput)
				console.log('StepForm Save After=', this.record.data);

			let results = await this.tableDef.validateRecord(this.record, this.fieldsEnabled);
			if (results.length > 0)
			{
				// Form has validation problems, don't save to server
				this.evOnSave.emit(results);
				return results;
			}

			results = await this.validateAdditionalConditions(ignore_warnings);
			if (results.length > 0)
			{
				// Form has validation problems, don't save to server
				this.evOnSave.emit(results);
				return results;
			}

			this.scoreForm();

			if (/Task/i.test(this.tableDef.tableName))
			{
				this.record.data.reported_dt = new Date();
				this.record.data.reported_by = LogicEngine.currentUser;
			}

			// Save the record.
			// Todo, this should have some validation to make sure there isn't
			// a network issue or a server issue
			let result = await this.record.saveIfNeeded();

			// Update lock_status property to unlocked
			if (this.lock_status)
			{
				this.lock_status.form_edit_message = "";
				this.lock_status.lock_by = "";
				this.lock_status.lock_dt = undefined;
				this.lock_status.lock_status = false;
			}

			const currentTask = this.logicRef?.getVariable('task_id');
			if (currentTask)
			{
				let task = await TaskManager.instance().getTaskDataById(currentTask);
				// Mark the task as complete
				await task.setStatus('C', 'Task closed automatically by submitting eCRF Form id=' + this.record.data.id);

				const shouldAutoComplete = this.logicRef?.process(`equals(auto_review,"Yes","yes")`);
				const isLoggedIn = LogicEngine.currentUser != "";
				if (shouldAutoComplete && isLoggedIn)
				{
					if (LogicEngine.debugOutput)
						console.log("StepForm Save: AutoReview Task because auto_review is set and someone is logged in.");
					await task.setStatus("R", "Task auto-reviewed because it was entered by site staff");
				}
			}

			this.evOnSave.emit(results);

			/** Todo, Write to Database using API */
			return results;
		} else
		{
			let results: FieldError[] = [];
			this.evOnSave.emit(results);
			return results;
		}
	}

	/**
	 * Save changed to an edit form
	 * @param fieldName
	 * @param userInputValue
	 * @param reason
	 */
	async saveQueryField(fieldName: string, reason: string): Promise<void>
	{
		if (!this.record) return;

		console.log('StepFrom saveQueryField(', fieldName, ')');

		let pid = '';
		if (this.logicRef)
		{
			pid = this.logicRef.process('participant_uuid');

			/** Create the audit record */
			const query = new QueryLog(pid);
			query.setMessage(reason);
			query.setTableName(this.record.tableName, this.record.data.id, fieldName);
			query.setStatus(QueryLog.QueryStatus.Open);
			query.setQueryType(QueryLog.QueryTypes.HumanQuery);
			query.setReferenceTaskId(this.logicRef?.getVariable('task_id'));
			await query.save();

			if (this.lock_status)
			{
				this.lock_status.form_edit_message = "";
				this.lock_status.lock_by = "";
				this.lock_status.lock_dt = undefined;
				this.lock_status.lock_status = false;
			}

			if (this.tableDef && this.logicRef && this.record)
			{
				const result = await this.record.saveIfNeeded();
			}
		}
	}

	/**
	 * Save Query Reply
	 * @param id A id for query to reply
	 * @param message A reply message
	 */
	async saveQueryReply(id: string, message: string): Promise<void>
	{
		if (!this.record) return;

		if (LogicEngine.debugOutput)
			console.log('StepForm saveQueryReply(', id, ')');

		// Search by ID
		let records = await DataQuery.instance().executeQuery('Query', 'id=' + id);
		// not found
		if (records.length === 0)
		{
			return;
		}

		const date = new Date();
		const newData = {
			query_status: QueryLog.QueryStatus.Answered,
			response: message,
			response_dt: date,
			response_timezone: date.getTimezoneOffset(),
			response_user: LogicEngine.currentUser,
		};

		records[0].setData(newData);
		await records[0].saveIfNeeded(true);
	}

	/**
	 * Close the query
	 * @param id A id for query to reply
	 * @param disposition A status for the query
	 * @param newMessage A message to start a new query
	 */
	async closeQuery(id: string, disposition: number | string, newMessage?: string): Promise<void>
	{
		if (!this.record) return;

		// Find query by ID
		let records = await DataQuery.instance().executeQuery('Query', 'id=' + id);
		// not found
		if (records.length === 0)
		{
			return;
		}
		// update query status
		const newData = {
			query_status: disposition,
		};

		records[0].setData(newData);
		records[0].saveIfNeeded(true);
		// start a new query
		if (disposition == QueryLog.QueryStatus.NotResolved && newMessage)
		{
			let pid = '';
			if (this.logicRef)
			{
				pid = this.logicRef.process('participant_uuid');

				/** Create the audit record */
				const query = new QueryLog(pid);
				query.setTableName(
					this.record.tableName,
					this.record.data.id,
					records[0].data.table_field,
				);
				query.setStatus(QueryLog.QueryStatus.Open);
				query.setQueryType(QueryLog.QueryTypes.HumanQuery);
				query.setReferenceTaskId(this.logicRef?.getVariable('task_id'));
				query.setMessage(newMessage);
				await query.save();
			}
		}
	}

	/**
	 * Save changed to an edit form
	 * @param fieldName
	 * @param userInputValue
	 * @param reason
	 */
	async saveEditField(fieldName: string, userInputValue: string, reason: string,): Promise<FieldError[]>
	{
		if (!this.record) return [];

		const oldValue = this.record.data[fieldName];
		const previousValues = this.record.getValueFormatted(fieldName);

		if (LogicEngine.debugOutput)
			console.log('StepForm saveEditField(', fieldName, userInputValue, ')');

		this.setValueFromUser(fieldName, userInputValue);
		const results = await this.save(true);
		if (results.length > 0)
		{
			this.record.data[fieldName] = oldValue;
			return results;
		}

		let pid = '';
		if (this.logicRef) pid = this.logicRef.process('participant_uuid');

		/** Create the audit record */
		const auditText = `Record field '${fieldName}' changed to '${userInputValue}' with reason '${reason}'`;
		const audit = new AuditLog(pid, auditText);
		audit.setTableName(this.record.tableName, this.record.data.id, fieldName);
		audit.setEventType(AuditLog.EventTypes.FieldChange);
		audit.setChangeValues(previousValues, this.record.getValueFormatted(fieldName));
		audit.setReasonText(reason);
		audit.setReferenceTaskId(this.logicRef?.getVariable('task_id'));
		await audit.save();

		return results;
	}

	/** Save multiple fields at once in edit mode.
	 * @param values [any] An object with the form values to save
	 * @param reason [string] The change reason for audit.
	 * @returns A list of validation errors.
	 */
	async saveEditFields(values: any, reason: string): Promise<FieldError[]>
	{
		if (!this.record) return [];

		// Keep a copy of the old data incase validation fails
		let oldValues: any = {};
		Object.assign(oldValues, this.record.data);

		// Reference to a list of formatted values before change
		let previousValues: { [key: string]: string } = {};

		for (let fieldName of Object.keys(values))
		{
			const userInputValue = values[fieldName];
			previousValues[fieldName] = this.record.getValueFormatted(fieldName);

			if (LogicEngine.debugOutput)
				console.log('StepForm saveEditFields(', fieldName, '=', userInputValue, ')');

			this.setValueFromUser(fieldName, userInputValue);
		}

		const results = await this.save(true);
		if (results.length > 0)
		{
			Object.assign(this.record.data, oldValues);
			return results;
		}

		let pid = '';
		if (this.logicRef) pid = this.logicRef.process('participant_uuid');

		for (let fieldName of Object.keys(values))
		{
			const userInputValue = values[fieldName];

			/** Create the audit record */
			const auditText = `Record field '${fieldName}' changed to '${userInputValue}' with reason '${reason}'`;
			const audit = new AuditLog(pid, auditText);
			audit.setTableName(this.record.tableName, this.record.data.id, fieldName);
			audit.setEventType(AuditLog.EventTypes.FieldChange);
			audit.setChangeValues(
				previousValues[fieldName],
				this.record.getValueFormatted(fieldName),
			);
			audit.setReasonText(reason);
			audit.setReferenceTaskId(this.logicRef?.getVariable('task_id'));
			await audit.save();

			/* Set the updated value */
			this.record.data[fieldName] = userInputValue;
		}

		return results;
	}

	/**
	 * Set a form value based on user input.
	 * @param fieldName
	 * @param userInputValue
	 */
	setValueFromUser(fieldName: string, userInputValue: string)
	{
		if (LogicEngine.debugOutput)
			console.log('StepForm setValueFromUser(', fieldName, ')=', userInputValue);

		if (!this.logicRef)
		{
			throw 'Missing logicRef from setValueFromUser';
		}

		if (this.tableDef)
		{
			const type = this.tableDef.getFieldType(fieldName);
			const reBrace = new RegExp('{{');

			if (this.record)
			{
				const currentValue = this.record.data[fieldName];

				this.record.data[fieldName] = userInputValue;
				const newValue = this.record.getValueDatabase(fieldName);
				// console.log("setValueFromUser DatabaseValue=", newValue);

				if (newValue !== currentValue && this.logicRef)
				{
					const pid = this.logicRef.getVariable('participant_uuid');
					if (pid && typeof pid === 'string' && pid.length > 0)
					{
						if (currentValue && (typeof currentValue !== 'string' || (currentValue != '' && !reBrace.test(currentValue))))
						{
							//  Value did change
							const auditNote = this.record.data.audit_note
								? ' Notes: ' + this.record.data.audit_note
								: '';
							const auditText = `Record field '${fieldName}' changed from '${currentValue}' to '${newValue}' in table '${this.tableDef.tableName}' with id '${this.record.data.id}.` + auditNote;
							const audit = new AuditLog(pid, auditText);
							audit.log.event_type = AuditLog.EventTypes.FieldChange;
							audit.setTableName(this.record.tableName, this.record.data.id, fieldName);
							audit.setChangeValues(currentValue, newValue);
							audit.setReferenceTaskId(this.logicRef?.getVariable('task_id'));
							audit.save();
						}
					}
				}

				this.record.data[fieldName] = newValue;

				if (this.logicRef)
				{
					const dbValue = this.record.getValueDatabase(fieldName);
					this.logicRef.assign(fieldName, dbValue);
				}
			}
		} else
		{
			console.log('StepForm setValueFromUser Unknowng type');
		}
	}

	/** Add a tab to the list of fields we're tracking in this holder */
	async addField(step_raw: PageStepType): Promise<StepForm.FieldInfo | undefined>
	{
		if (!this.logicRef) throw 'Exception: should not happen but logicRef in StepForm is null';

		const step = new StepFormField(step_raw);

		if (step.hasOptions("Auto"))
		{
			if (step.field_id && step.field_id === 'uuid' && this.tableDef)
			{
				let uuid = await this.tableDef.generate3dash4();
				this.setValueFromUser(step.field_id, uuid);
			}
		} else if (step.hasOptions('Set'))
		{
			/** Set commands on a field assign a fixed value to the object.
			 * The field to assign is listed in "Field Id" column.
			 * The value to assign comes from the option 1 column.
			 * The value in option 1 needs to go through LogicEngine though.
			 */
			const value = this.logicRef.processTextReplacement(step.option_1 ? step.option_1 : '');
			if (step.field_id)
			{
				this.setValueFromUser(step.field_id, value);
			}
		} else
		{
			/** By default, steps in the form are added to the list and passed to the render */

			if (step.hasOptions('Button'))
			{
				if (this.logicRef.fnHasGroup('DataMonitor'))
				{
					console.log('StepForm Skipping Button [' + step.text + '] due to Data Monitor Mode.');
					return undefined;
				}

				// Remove spaces from link
				if (step.option_1)
				{
					step.option_1 = step.option_1.replace(" ?", "?");
					step.option_1 = step.option_1.replace("? ", "?");

					//  Create a random value for cache bypass
					let letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
					let code = "";
					for (let x = 0; x < 8; x++)
					{
						let num = Math.floor(Math.random() * letters.length);
						code += letters.charAt(num);
					}

					let reNoCache = new RegExp("XX[A-Z]{6,9}.", "g");
					step.option_1 = step.option_1.replace(reNoCache, "");

					// Addf the random value to the page link to remove the cache
					step.option_1 = step.option_1.replace("/page/", "/page/XX" + code + "/");
				}
			}

			const field: StepForm.FieldInfo = {
				text: this.logicRef.processTextReplacement(step.text ? step.text : ''),
				type: step.options,
				field: step.field_id ? step.field_id : '',
				option1: step.option_1 ? step.option_1 : '',
				option2: step.option_2 ? step.option_2 : '',
			};

			if (this.logicRef.fnHasGroup('DataMonitor'))
			{
				field.enabled = false;
			}

			this.fields.push(field);
			return field;
		}
	}

	/** Returns true if the review flag should be on */
	async getReviewStatus(): Promise<boolean>
	{
		// Find the task
		let currentTask = this.logicRef?.getVariable('task_id');
		if (!currentTask) currentTask = this.logicRef?.getVariable('last_task_id');
		if (!currentTask) currentTask = this.logicRef?.getVariable('Tasks_id');

		if (currentTask)
		{
			let task = await TaskManager.instance().getTaskDataById(currentTask);

			if (LogicEngine.debugOutput)
				console.log("StepForm getReviewStatus for task", currentTask, " record=", task.record.data);

			if (task.record.data.task_status != 'R') return false;
			return true;
		} else
		{
			console.error("StepForm setReviewStatus unable to find task_id, last_task_id, or Tasks_id to update.");
			return false;
		}
	}

	/** Update the review status on a record to the newReviewStatus value.
	Should be set to false if the data in the form changes.  Should be set to true from the UI.

	The record should have these fields in Data Dictionary defined
	reviewed_by
	reviewed_dt

	The record MAY have this set to a boolean type
	review_status

	*/
	async setReviewStatus(newReviewStatus: boolean)
	{
		// Find the task
		let currentTask = this.logicRef?.getVariable('task_id');
		if (!currentTask) currentTask = this.logicRef?.getVariable('last_task_id');
		if (!currentTask) currentTask = this.logicRef?.getVariable('Tasks_id');

		if (LogicEngine.debugOutput)
			console.log("StepFrom setReviewStatus ", newReviewStatus, " of task ", currentTask);

		if (currentTask)
		{
			let task = await TaskManager.instance().getTaskDataById(currentTask);
			if (task.record.data.task_status != 'R' && newReviewStatus)
				await task.setStatus('R', 'Task was marked as reviewed');

			if (task.record.data.task_status == 'R' && !newReviewStatus)
				await task.setStatus('C', 'Task was marked as complete because review was removed');

		} else
		{
			console.error("StepForm setReviewStatus unable to find task_id, last_task_id, or Tasks_id to update.");
			return;
		}

		if (newReviewStatus)
		{
			this.setValueFromUser("reviewed_by", LogicEngine.currentUser);
			this.setValueFromUser("reviewed_dt", new Date().toString());
			this.setValueFromUser("is_reviewed", "Yes");

			if (this.tableDef && this.logicRef && this.record)
			{
				const result = await this.record.saveIfNeeded();
			}

		} else
		{
			//  Check to see if it is already marked as not reviewed.
			if (DataTools.internalValidateString(this.record?.data.reviewed_by) === '')
				return true;

			//  Take away the review
			this.setValueFromUser("reviewed_by", "");
			this.setValueFromUser("reviewed_dt", "");
			this.setValueFromUser("is_reviewed", "No");

			if (this.tableDef && this.logicRef && this.record)
			{
				const result = await this.record.saveIfNeeded();
			}
		}
	}

	/**
	 * Toggle lock status of form
	 * @param lock_status
	 */
	async toggleLockForm(
		lock_status: boolean
	): Promise<boolean>
	{
		let updated_lock_status: StepFromLockStatus | undefined = this.lock_status;
		if (lock_status)
		{
			this.setValueFromUser('lock_by', LogicEngine.currentUser);
			this.setValueFromUser('lock_dt', new Date().toString());
			this.setValueFromUser('lock_status', "Yes");

			if (updated_lock_status)
			{
				updated_lock_status = {
					...updated_lock_status,
					form_edit_message: "This form is locked and can't be edited by the site unless you unlock it.",
					lock_by: LogicEngine.currentUser,
					lock_dt: new Date(),
					lock_status: lock_status
				};
			}
		}
		else
		{
			this.setValueFromUser('lock_by', '');
			this.setValueFromUser('lock_dt', '');
			this.setValueFromUser('lock_status', "No");

			if (updated_lock_status)
			{
				updated_lock_status = {
					...updated_lock_status,
					form_edit_message: "",
					lock_by: '',
					lock_dt: undefined,
					lock_status: lock_status
				};
			}
		}

		if (this.tableDef && this.logicRef && this.record)
		{
			const result = await this.record.saveIfNeeded();

			if (result)
			{
				this.lock_status = updated_lock_status;
			}
			return result;
		}

		return false;
	}


	onFieldChange(option: StepConditional.FieldEnabledEvent)
	{
		this.fieldsEnabled[option.field] = option.enabled;
	}

	/** Validation Step being added to form submit */
	addCondition(option: StepConditional)
	{
		this.conditions.push(option);
	}

	/** Validation step for scoring */
	addScoring(option: StepConditional)
	{
		this.scoring.push(option);
	}

	/** Load the history (AuditLog.EventTypes.FieldChange), queries (QueryLog) and lock history (AuditLog.EventTypes.RecordLock) based on the table */
	async loadFormHistory(logic: LogicEngine): Promise<boolean>
	{
		if (!this.record) return false;
		if (!this.record.data) return false;

		if (this.record.tableName && this.record.tableName.startsWith("#"))
		{
			if (LogicEngine.debugOutput)
				console.log('StepForm Loading formHistory: loadFormHistory for temp table skipped');
			return false;
		}

		const uuid: string = logic.process('participant_uuid');

		// Load audit records
		const dq = DataQuery.instance();
		let queryText = 'event_type=' + AuditLog.EventTypes.FieldChange;
		queryText += '&participant_id=' + uuid;
		queryText += '&table_name=' + this.record.tableName;
		queryText += '&reference_id=' + this.record.data.id;

		if (LogicEngine.debugOutput)
			console.log('StepForm Loading formHistory:', queryText);

		this.formHistory.push(...(await dq.executeQuery('Audit', queryText, false)));

		// Load Query records
		queryText = 'participant_id=' + uuid;
		queryText += '&table_name=' + this.record.tableName;
		queryText += '&reference_id=' + this.record.data.id;

		if (LogicEngine.debugOutput)
			console.log('StepForm Loading formQuery:', queryText);

		this.formQuery.push(...(await dq.executeQuery('Query', queryText, false)));

		// Load lock records
		queryText = 'event_type=' + AuditLog.EventTypes.RecordLock;
		queryText += '&participant_id=' + uuid;
		queryText += '&table_name=' + this.record.tableName;
		queryText += '&reference_id=' + this.record.data.id;

		if (LogicEngine.debugOutput)
			console.log('StepForm Loading formHistory:', queryText);

		this.lockHistory.push(...(await dq.executeQuery('Audit', queryText, false)));

		return true;
	}

	/** This is called when the "Begin Form" command is found.
	 * It should create a new record of a given type or find an existing record
	 */
	async processStep(logic: LogicEngine): Promise<boolean>
	{
		this.logicRef = logic;
		await super.processStep(logic);

		this.fieldsEnabled = {};

		this.logicRef.evFieldChanged.subscribe(this.onFieldChange.bind(this));

		// Table Name is configured in the "Step Option" column
		let tableName = this.options.value;

		if (tableName.trim() === "")
		{
			console.error("StepForm processStep missing table name in:", this);
			tableName = '#Temp';
		}

		// Get reference to the DataDictionary table
		const dataService = DataServices.instance();
		this.dataDictionary = await dataService.getDataDictionary();
		this.tableDef = this.dataDictionary.getTable(tableName);
		this.record = undefined;

		// Determine if there is a current task id set
		const task_id = logic.getVariable('task_id');
		const myTaskTitle = logic.getVariable('task_title');

		// Check to see if this is special edit mode
		if (logic.process("equal(special_mode,'edit','review')")) this.is_edit_mode = true;

		// Check to see if this is special edit mode
		if (logic.process("equal(special_mode,'query')")) this.is_query_mode = true;

		//
		if (task_id && myTaskTitle && typeof task_id === 'string')
		{
			// Determine if there is already a record existing with this ID
			// console.log("Finding ", tableName, " taskId=", task_id, " for myTitle=", myTaskTitle);
			let existingList = await DataQuery.instance().executeQuery(
				tableName,
				`task_id=${task_id}`,
			);

			if (existingList.length === 0)
				existingList = await DataQuery.instance().executeQuery(tableName, `id=${task_id}`);

			if (existingList.length > 0)
			{
				for (let existing of existingList)
				{
					// console.log("Compare",existing.data.task_title,myTaskTitle);
					if (existing.data.task_title === myTaskTitle)
					{
						if (await existing.fixRecord())
						{
							this.record = existing;
							// console.log("Assign existing task for ", existing);
						}
					}
				}
			}
		}

		// CHeck for Edit ID as optional in option_1
		if (this.option_1 && this.option_1 !== '')
		{
			let existingId = logic.processTextReplacement(this.option_1);
			let existingList = await DataQuery.instance().executeQuery(
				tableName,
				`id=${existingId}`,
			);
			if (existingList.length > 0)
			{
				let existing = existingList[0];
				if (await existing.fixRecord())
				{
					this.record = existing;
				}
			}
		}

		// Convert the raw JSON into a DataRecord type

		/**
		 * TODO:  This assumes we are using a new record.
		 * If the Option field contains an ID then we need to load it here instead
		 * of createing a new record.
		 */

		if (!this.record)
		{
			this.record = this.dataDictionary.getRecordFromObject(tableName, {});
			this.record.validateId();
		}

		if (this.record) await this.record.getTableDef();

		/** Task Common fields are required for task tables */
		if (task_id && typeof task_id === 'string')
		{
			this.record.data.participant_id = logic.getVariable('participant_uuid');
			this.record.data.created_dt = new Date();
			this.record.data.created_by = LogicEngine.currentUser;
			this.record.data.task_id = task_id;
			this.record.data.task_title = logic.getVariable('task_title');
		}

		/** Import the existing record values into the logic engine.
		 * For each field of the DataDictionary, a matching variable in the LogicEngine
		 * is set to the value from this record.
		 */

		// console.log("StepForm processSet imported record into logic engine:", this.record?.data);
		this.logicRef?.import(this.record?.data);

		if (this.record && this.record.data)
		{
			let tableNameID = this.record.tableName + '_id';
			tableNameID = tableNameID.toLocaleLowerCase();

			if (LogicEngine.debugOutput)
				console.log('StepForm Table ID=', tableNameID, '=', this.record.data.id);

			this.logicRef.assign(tableNameID, this.record.data.id);

			await this.loadFormHistory(logic);

			//  Check to see if we can show the lock
			if (this.is_query_mode && this.record.data.pi_lock_by !== undefined)
			{
				this.lock_status = {
					show_pi_lock: false, //  Currently not using this feature
					show_edit_lock: true, // Show lock feature in query_mode
					form_edit_allow: true, // Doesn't really apply in query mode, no edit screen
					form_edit_message: 'You are accessing this form in DataMonitor mode.',
					pi_lock_by: this.record.data.pi_lock_by,
					pi_lock_dt: this.record.data.pi_lock_dt,
					lock_by: this.record.data.lock_by,
					lock_dt: this.record.data.lock_dt,
					lock_status: this.record.data.lock_status
				};

				if (this.lock_status.lock_status)
				{
					this.lock_status.form_edit_message = "This record is locked and can't be edited by the site unless you unlock it.";
				}
			} else
			{
				this.lock_status = {
					show_pi_lock: false, //  Currently not using this feature
					show_edit_lock: false, // Show lock feature in query_mode
					form_edit_allow: true, // Doesn't really apply in query mode, no edit screen
					form_edit_message: '',
					pi_lock_by: this.record.data.pi_lock_by,
					pi_lock_dt: this.record.data.pi_lock_dt,
					lock_by: this.record.data.lock_by,
					lock_dt: this.record.data.lock_dt,
					lock_status: this.record.data.lock_status
				};

				if (this.lock_status.lock_status)
				{
					this.lock_status.form_edit_allow = false;
					this.lock_status.form_edit_message = "This record is locked and can't be edited.";
				}
			}

		}

		return true;
	}
}

export namespace StepForm
{
	export interface FieldInfo
	{
		text: string;
		type: StringOptions;
		field: string;
		enabled?: boolean;
		option1: string;
		option2: string;
	}
}
